import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  TrendingUp, 
  Activity, 
  Shield,
  Users,
  MapPin,
  Wind,
  Microscope,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { riskAssessmentEngine } from '../../services/riskAssessmentEngine';
import { getRiskLevelConfig, formatRiskScore } from '../../types/risk';
import './RiskAssessmentDashboard.css';

/**
 * Risk Assessment Dashboard
 * Displays comprehensive risk assessment with all factors and recommendations
 */
const RiskAssessmentDashboard = ({ 
  baseRisk, 
  context, 
  onAssessmentComplete,
  showRecommendations = true 
}) => {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    factors: true,
    recommendations: true,
    details: false
  });
  
  // Calculate risk assessment
  useEffect(() => {
    const calculateAssessment = async () => {
      if (!baseRisk) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const result = await riskAssessmentEngine.calculateComprehensiveRisk(baseRisk, context);
        setAssessment(result);
        
        if (onAssessmentComplete) {
          onAssessmentComplete(result);
        }
      } catch (err) {
        console.error('Error calculating risk assessment:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    calculateAssessment();
  }, [baseRisk, JSON.stringify(context)]);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  if (loading) {
    return (
      <div className="risk-assessment-loading">
        <Activity size={32} className="spinner-icon" />
        <p>Calculating comprehensive risk assessment...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="risk-assessment-error">
        <AlertTriangle size={32} color="#f44336" />
        <p>Error calculating risk: {error}</p>
      </div>
    );
  }
  
  if (!assessment) {
    return null;
  }
  
  const riskConfig = getRiskLevelConfig(assessment.riskLevel);
  
  return (
    <div className="risk-assessment-dashboard">
      {/* Risk Score Header */}
      <div 
        className="risk-score-header"
        style={{ 
          backgroundColor: riskConfig.bgColor,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}
      >
        <div className="risk-score-main">
          <div className="risk-icon">{riskConfig.icon}</div>
          <div className="risk-score-info">
            <div className="risk-level-label" style={{ color: riskConfig.color }}>
              {riskConfig.label} Risk
            </div>
            <div className="risk-score-value">
              {formatRiskScore(assessment.finalScore)}
            </div>
            <div className="base-rpn-info">
              Base RPN: {assessment.baseRPN} → Final Score: {assessment.finalScore}
            </div>
          </div>
        </div>
        <div className="risk-description">
          {riskConfig.description}
        </div>
      </div>
      
      {/* Risk Factors Breakdown */}
      <div className="risk-section">
        <div 
          className="risk-section-header"
          onClick={() => toggleSection('factors')}
        >
          <div className="section-title">
            <TrendingUp size={20} />
            <h3>Risk Factor Multipliers</h3>
          </div>
          <button className="toggle-btn">
            {expandedSections.factors ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
        
        {expandedSections.factors && (
          <div className="risk-section-content">
            <div className="multipliers-grid">
              {/* Location Factor */}
              <div className="multiplier-card">
                <div className="multiplier-icon">
                  <MapPin size={24} color="#2196F3" />
                </div>
                <div className="multiplier-info">
                  <div className="multiplier-label">Location</div>
                  <div className="multiplier-value">
                    ×{assessment.factors.location.toFixed(2)}
                  </div>
                  <div className="multiplier-description">
                    Department & room characteristics
                  </div>
                </div>
              </div>
              
              {/* Patient Factor */}
              <div className="multiplier-card">
                <div className="multiplier-icon">
                  <Users size={24} color="#9c27b0" />
                </div>
                <div className="multiplier-info">
                  <div className="multiplier-label">Patient</div>
                  <div className="multiplier-value">
                    ×{assessment.factors.patient.toFixed(2)}
                  </div>
                  <div className="multiplier-description">
                    Patient risk factors
                    {assessment.appliedDetails?.patientRiskFactorIds?.length > 0 && 
                      ` (${assessment.appliedDetails.patientRiskFactorIds.length} factors)`
                    }
                  </div>
                </div>
              </div>
              
              {/* Environmental Factor */}
              <div className="multiplier-card">
                <div className="multiplier-icon">
                  <Wind size={24} color="#4caf50" />
                </div>
                <div className="multiplier-info">
                  <div className="multiplier-label">Environmental</div>
                  <div className="multiplier-value">
                    ×{assessment.factors.environmental.toFixed(2)}
                  </div>
                  <div className="multiplier-description">
                    Environmental conditions
                    {assessment.appliedDetails?.environmentalFactorIds?.length > 0 && 
                      ` (${assessment.appliedDetails.environmentalFactorIds.length} factors)`
                    }
                  </div>
                </div>
              </div>
              
              {/* Biological Factor */}
              <div className="multiplier-card">
                <div className="multiplier-icon">
                  <Microscope size={24} color="#ff9800" />
                </div>
                <div className="multiplier-info">
                  <div className="multiplier-label">Biological</div>
                  <div className="multiplier-value">
                    ×{assessment.factors.biological.toFixed(2)}
                  </div>
                  <div className="multiplier-description">
                    Biological entities
                    {assessment.appliedDetails?.biologicalEntityIds?.length > 0 && 
                      ` (${assessment.appliedDetails.biologicalEntityIds.length} entities)`
                    }
                  </div>
                </div>
              </div>
            </div>
            
            {/* Calculation Formula */}
            <div className="calculation-formula">
              <Info size={16} />
              <span>
                Final Score = Base RPN ({assessment.baseRPN}) 
                × Location ({assessment.factors.location.toFixed(2)}) 
                × Patient ({assessment.factors.patient.toFixed(2)}) 
                × Environmental ({assessment.factors.environmental.toFixed(2)}) 
                × Biological ({assessment.factors.biological.toFixed(2)}) 
                = <strong>{assessment.finalScore}</strong>
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Recommendations */}
      {showRecommendations && assessment.recommendations && assessment.recommendations.length > 0 && (
        <div className="risk-section">
          <div 
            className="risk-section-header"
            onClick={() => toggleSection('recommendations')}
          >
            <div className="section-title">
              <Shield size={20} />
              <h3>Recommendations ({assessment.recommendations.length})</h3>
            </div>
            <button className="toggle-btn">
              {expandedSections.recommendations ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.recommendations && (
            <div className="risk-section-content">
              <div className="recommendations-list">
                {assessment.recommendations.map((rec, index) => (
                  <div 
                    key={index} 
                    className={`recommendation-card priority-${rec.priority.toLowerCase()}`}
                  >
                    <div className="recommendation-icon">
                      {rec.icon}
                    </div>
                    <div className="recommendation-content">
                      <div className="recommendation-header">
                        <span className={`priority-badge priority-${rec.priority.toLowerCase()}`}>
                          {rec.priority}
                        </span>
                        <span className="recommendation-type">
                          {rec.type}
                        </span>
                      </div>
                      <div className="recommendation-action">
                        {rec.action}
                      </div>
                      <div className="recommendation-rationale">
                        {rec.rationale}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Detailed Breakdown */}
      {assessment.appliedDetails && (
        <div className="risk-section">
          <div 
            className="risk-section-header"
            onClick={() => toggleSection('details')}
          >
            <div className="section-title">
              <Activity size={20} />
              <h3>Detailed Breakdown</h3>
            </div>
            <button className="toggle-btn">
              {expandedSections.details ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.details && (
            <div className="risk-section-content">
              {/* Patient Risk Factors Applied */}
              {assessment.appliedDetails.patientDetails?.factorsApplied?.length > 0 && (
                <div className="detail-section">
                  <h4>Patient Risk Factors Applied</h4>
                  <ul>
                    {assessment.appliedDetails.patientDetails.factorsApplied.map(factor => (
                      <li key={factor.id}>
                        <strong>{factor.name}</strong> ({factor.category}) - ×{factor.multiplier}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Environmental Factors Applied */}
              {assessment.appliedDetails.environmentalDetails?.factorsApplied?.length > 0 && (
                <div className="detail-section">
                  <h4>Environmental Risk Factors Applied</h4>
                  <ul>
                    {assessment.appliedDetails.environmentalDetails.factorsApplied.map(factor => (
                      <li key={factor.id}>
                        <strong>{factor.name}</strong> ({factor.category}) - ×{factor.multiplier}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Location Details */}
              {assessment.appliedDetails.locationInfo && (
                <div className="detail-section">
                  <h4>Location Risk Factors</h4>
                  <ul>
                    {assessment.appliedDetails.locationInfo.departmentImpact > 0 && (
                      <li>Department Impact: ×{assessment.appliedDetails.locationInfo.departmentImpact}</li>
                    )}
                    {assessment.appliedDetails.locationInfo.roomImpact > 0 && (
                      <li>Room Impact: ×{assessment.appliedDetails.locationInfo.roomImpact}</li>
                    )}
                    {assessment.appliedDetails.locationInfo.isolationImpact > 0 && (
                      <li>Isolation Impact: ×{assessment.appliedDetails.locationInfo.isolationImpact}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentDashboard;


